import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { User, Mail, Phone, Home, Shield, Eye, EyeOff, Save, UserX } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Perfil = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: errorsProfile }
  } = useForm({
    defaultValues: {
      nome: user?.nome || '',
      telefone: user?.telefone || '',
      email: user?.email || ''
    }
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword },
    reset: resetPassword
  } = useForm();

  const onSubmitProfile = async (data) => {
    setLoading(true);
    const result = await updateProfile(data);
    setLoading(false);
    
    if (result.success) {
      toast.success('Perfil atualizado com sucesso!');
    }
  };

  const onSubmitPassword = async (data) => {
    setLoading(true);
    const result = await changePassword(data.senhaAtual, data.novaSenha);
    setLoading(false);
    
    if (result.success) {
      toast.success('Senha alterada com sucesso!');
      setShowChangePassword(false);
      resetPassword();
    }
  };

  const getTipoLabel = (tipo) => {
    const tipoMap = {
      sindico: 'Síndico',
      subsindico: 'Subsíndico',
      morador: 'Morador',
      porteiro: 'Porteiro',
      administrador: 'Administrador'
    };
    return tipoMap[tipo] || tipo;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gerencie suas informações pessoais
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-body text-center">
              <div className="mx-auto h-24 w-24 rounded-full bg-primary-600 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">
                  {user?.nome?.charAt(0).toUpperCase()}
                </span>
              </div>
              <h3 className="text-lg font-medium text-gray-900">{user?.nome}</h3>
              <p className="text-sm text-gray-500">{user?.email}</p>
              <div className="mt-4">
                <span className="badge badge-info">
                  <Shield className="h-3 w-3 mr-1" />
                  {getTipoLabel(user?.tipo)}
                </span>
              </div>
            </div>
          </div>

          <div className="card mt-6">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Informações do Condomínio</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Home className="h-4 w-4 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {user?.condominio?.nome}
                    </p>
                    <p className="text-sm text-gray-500">
                      {user?.apartamento ? 
                        `${user.apartamento.bloco} - ${user.apartamento.numero}` : 
                        'Sem apartamento'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-6">
            <div className="card-body">
              <Link
                to="/exclusao-conta"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <UserX className="h-4 w-4" />
                Solicitar exclusão de conta e dados
              </Link>
              <p className="mt-2 text-xs text-gray-500">
                Saiba como solicitar a exclusão da sua conta e dos seus dados pessoais.
              </p>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Informações Pessoais</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="label">
                      <User className="h-4 w-4 inline mr-2" />
                      Nome Completo
                    </label>
                    <input
                      {...registerProfile('nome', {
                        required: 'Nome é obrigatório'
                      })}
                      type="text"
                      className={errorsProfile.nome ? 'input-error' : 'input'}
                    />
                    {errorsProfile.nome && (
                      <p className="mt-1 text-sm text-red-600">{errorsProfile.nome.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Telefone
                    </label>
                    <input
                      {...registerProfile('telefone', {
                        required: 'Telefone é obrigatório',
                        pattern: {
                          value: /^\(\d{2}\)\s\d{4,5}-\d{4}$/,
                          message: 'Telefone deve estar no formato (11) 99999-9999'
                        }
                      })}
                      type="text"
                      className={errorsProfile.telefone ? 'input-error' : 'input'}
                      placeholder="(11) 99999-9999"
                    />
                    {errorsProfile.telefone && (
                      <p className="mt-1 text-sm text-red-600">{errorsProfile.telefone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">
                    <Mail className="h-4 w-4 inline mr-2" />
                    Email
                  </label>
                  <input
                    {...registerProfile('email', {
                      required: 'Email é obrigatório',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Email inválido'
                      }
                    })}
                    type="email"
                    className={errorsProfile.email ? 'input-error' : 'input'}
                    disabled
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    O email não pode ser alterado. Entre em contato com o administrador.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {loading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Change Password */}
          <div className="card mt-6">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Alterar Senha</h3>
                <button
                  onClick={() => setShowChangePassword(!showChangePassword)}
                  className="btn-secondary"
                >
                  {showChangePassword ? 'Cancelar' : 'Alterar Senha'}
                </button>
              </div>
            </div>
            
            {showChangePassword && (
              <div className="card-body">
                <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-6">
                  <div>
                    <label className="label">Senha Atual</label>
                    <div className="relative">
                      <input
                        {...registerPassword('senhaAtual', {
                          required: 'Senha atual é obrigatória'
                        })}
                        type={showPassword ? 'text' : 'password'}
                        className={errorsPassword.senhaAtual ? 'input-error pr-10' : 'input pr-10'}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errorsPassword.senhaAtual && (
                      <p className="mt-1 text-sm text-red-600">{errorsPassword.senhaAtual.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Nova Senha</label>
                    <input
                      {...registerPassword('novaSenha', {
                        required: 'Nova senha é obrigatória',
                        minLength: {
                          value: 6,
                          message: 'Senha deve ter pelo menos 6 caracteres'
                        }
                      })}
                      type="password"
                      className={errorsPassword.novaSenha ? 'input-error' : 'input'}
                    />
                    {errorsPassword.novaSenha && (
                      <p className="mt-1 text-sm text-red-600">{errorsPassword.novaSenha.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Confirmar Nova Senha</label>
                    <input
                      {...registerPassword('confirmarSenha', {
                        required: 'Confirmação de senha é obrigatória',
                        validate: (value) => {
                          const novaSenha = document.querySelector('input[name="novaSenha"]').value;
                          return value === novaSenha || 'Senhas não coincidem';
                        }
                      })}
                      type="password"
                      className={errorsPassword.confirmarSenha ? 'input-error' : 'input'}
                    />
                    {errorsPassword.confirmarSenha && (
                      <p className="mt-1 text-sm text-red-600">{errorsPassword.confirmarSenha.message}</p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading ? (
                        <LoadingSpinner size="sm" className="mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {loading ? 'Alterando...' : 'Alterar Senha'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Perfil;
